import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ExternalLink,
  Calendar,
  Image,
  Video,
  Briefcase,
  Users,
  Plus,
  Edit3,
  Globe,
  MapPin,
  Phone,
  Mail,
  Building2,
  Eye,
  Trash2,
  CheckCircle,
  AlertCircle,
  Clock,
  Play,
  X,
} from 'lucide-react';
import MediaCarousel from './MediaCarousel';
import MediaUpload from './MediaUpload';
import ClientForm from './ClientForm';
import ProjectForm from './ProjectForm';
import { useToast } from '../../hooks/useToast';
import { collection, getDocs, onSnapshot, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { firestore, storage } from '../../database/firebase';
import { deleteObject, ref } from 'firebase/storage';

import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';

export default function CompanyProfile({ organizationData }) {
  const [activeSection, setActiveSection] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [showMediaUpload, setShowMediaUpload] = useState(false);
  const [showMediaCarousel, setShowMediaCarousel] = useState(false);
  const [showProjectImageCarousel, setShowProjectImageCarousel] = useState(false);
  const [selectedProjectImages, setSelectedProjectImages] = useState([]);
  const [selectedProjectImageIndex, setSelectedProjectImageIndex] = useState(0);
  const [showClientForm, setShowClientForm] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [media, setMedia] = useState([]);
  const [clients, setClients] = useState([]);
  const [userClients, setUserClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [editingClient, setEditingClient] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [showClientView, setShowClientView] = useState(false);
  const [viewClient, setViewClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState({});

  const organizationId = organizationData?.organization || 'default';
  const currentUserId = getAuth()?.currentUser?.uid || null;

  useEffect(() => {
    if (organizationId) {
      loadData();
    }
  }, [organizationId]);

  const loadData = async () => {
    try {
      setLoading(true);

      try {
        try {
          const profileRef = doc(firestore, 'companyProfiles', organizationId);
          const clientRef = doc(firestore, 'clients', organizationId);
          const [profileSnap, clientSnap] = await Promise.all([
            getDoc(profileRef),
            getDoc(clientRef),
          ]);

          const profile = profileSnap.exists() ? profileSnap.data() : {};
          const client = clientSnap.exists() ? clientSnap.data() : {};

          setCompanyData({
            name: client.organization || organizationData?.organization || '',
            profileLink: profile.profileLink || '',
            launchDate: profile.launchDate || '',
            isLaunched: profile.isLaunched ?? false,
            description: profile.description || '',
            location: client.location || profile.location || '',
            phone: client.phone || profile.phone || '',
            email: client.email || profile.email || '',
            website: profile.website || '',
          });
        } catch (err) {
          setCompanyData({});
        }

        const mediaQuery = collection(firestore, 'companyProfiles', organizationId, 'media');
        const mediaUnsubscribe = onSnapshot(
          mediaQuery,
          snapshot => {
            const mediaData = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
            }));
            setMedia(mediaData);
          },
          error => {
            setMedia([]);
          }
        );

        const orgClientsCol = collection(firestore, 'companyProfiles', organizationId, 'clients');
        const orgClientsUnsubscribe = onSnapshot(
          orgClientsCol,
          snapshot => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, _source: 'org', ...doc.data() }));
            setClients(prev => {
              const merged = [...data, ...userClients];
              return merged;
            });
          },
          error => {}
        );

        let userClientsUnsubscribe = () => {};
        if (currentUserId) {
          const userClientsCol = collection(firestore, 'companyClient', currentUserId, 'clients');
          userClientsUnsubscribe = onSnapshot(
            userClientsCol,
            snapshot => {
              const data = snapshot.docs.map(doc => ({
                id: doc.id,
                _source: 'user',
                ...doc.data(),
              }));
              setUserClients(data);
              setClients(prev => {
                const orgOnly = prev.filter(c => c._source === 'org');
                return [...orgOnly, ...data];
              });
            },
            error => {
              toast.error('Error loading user clients:', error);
              setUserClients([]);
            }
          );
        }

        const projectsQuery = collection(firestore, 'companyProfiles', organizationId, 'projects');
        const projectsUnsubscribe = onSnapshot(
          projectsQuery,
          snapshot => {
            const projectsData = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
            }));
            setProjects(projectsData);
          },
          error => {
            toast.error('Error loading projects:', error);
            setProjects([]);
          }
        );

        return () => {
          try {
            mediaUnsubscribe();
            orgClientsUnsubscribe();
            if (currentUserId) userClientsUnsubscribe();
            projectsUnsubscribe();
          } catch (error) {
            toast.error('Error unsubscribing:', error);
          }
        };
      } catch (error) {
        toast.error('Error setting up listeners:', error);
        setMedia([]);
        setClients([]);
        setProjects([]);
      }
    } catch (error) {
      toast.error('Error loading data:', error);
      setMedia([]);
      setClients([]);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMediaUploadComplete = uploadedMedia => {
    setShowMediaUpload(false);
  };

  const handleClientAdded = newClient => {
    setShowClientForm(false);
    setEditingClient(null);
  };

  const handleProjectAdded = newProject => {
    setShowProjectForm(false);
    setEditingProject(null);
  };

  const handleDeleteMedia = async mediaId => {
    try {
      const mediaItem = media.find(m => m.id === mediaId);
      if (mediaItem?.url) {
        try {
          const fileRef = ref(storage, mediaItem.url);
          await deleteObject(fileRef);
        } catch (e) {
          toast.error('Failed to delete storage object, continuing to remove Firestore doc:', e);
          setMedia(media.filter(m => m.id !== mediaId));
        }
      }
      await deleteDoc(doc(firestore, 'companyProfiles', organizationId, 'media', mediaId));
    } catch (error) {
      toast.error('Error deleting media. Please try again.');
      setMedia(media.filter(m => m.id !== mediaId));
    }
  };

  const openMediaCarousel = index => {
    setSelectedMediaIndex(index);
    setShowMediaCarousel(true);
  };

  const openProjectImageCarousel = (project, imageIndex = 0) => {
    if (project.images && project.images.length > 0) {
      const projectMedia = project.images.map(img => ({
        url: img.url || img,
        caption: project.name,
        type: 'image',
      }));
      setSelectedProjectImages(projectMedia);
      setSelectedProjectImageIndex(imageIndex);
      setShowProjectImageCarousel(true);
    }
  };

  const sections = [
    { id: 'overview', label: 'Overview', icon: Building2 },
    { id: 'media', label: 'Photos & Videos', icon: Image },
    { id: 'projects', label: 'Projects', icon: Briefcase },
    { id: 'clients', label: 'Clients', icon: Users },
  ];

  const getStatusColor = status => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'in_progress':
        return 'bg-orange-100 text-orange-700';
      case 'planning':
        return 'bg-blue-100 text-blue-700';
      case 'on_hold':
        return 'bg-yellow-100 text-yellow-700';
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getKYCStatusColor = status => {
    switch (status) {
      case 'verified':
        return 'bg-green-100 text-green-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      case 'not_required':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="h-8 w-8 rounded-full border-4 border-slate-300 border-t-slate-600"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-2xl border bg-white p-4 shadow-sm md:p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900">{companyData.name}</h2>
            <p className="mt-1 text-slate-600">Company Profile & Information</p>
          </div>
        </div>

        {/* Quick Info Grid */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-blue-100 p-2">
              <Globe className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Profile Link</p>
              <a
                href={companyData.profileLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-1 text-slate-900 hover:text-blue-600"
              >
                <span>View Profile</span>
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-green-100 p-2">
              <Calendar className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Launch Date</p>
              <p className="text-slate-900">
                {companyData.isLaunched
                  ? new Date(companyData.launchDate).toLocaleDateString()
                  : 'Not Launched'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-purple-100 p-2">
              <MapPin className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Location</p>
              <p className="text-slate-900">{companyData.location}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-orange-100 p-2">
              <Phone className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Contact</p>
              <p className="text-slate-900">{companyData.phone}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="rounded-2xl border bg-white p-2 shadow-sm">
        <div className="flex space-x-1">
          {sections.map(section => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center space-x-2 rounded-lg px-4 py-2 transition-all ${
                  activeSection === section.id
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{section.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content Sections */}
      <div className="rounded-2xl border bg-white p-4 shadow-sm md:p-6">
        {activeSection === 'overview' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div>
              <h3 className="mb-3 text-lg font-semibold text-red-900">Company Description</h3>
              <p className="leading-relaxed text-slate-700">{companyData.description}</p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <h3 className="mb-3 text-lg font-semibold text-slate-900">Contact Information</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-700">{companyData.email}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-700">{companyData.phone}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Globe className="h-4 w-4 text-slate-400" />
                    <a
                      href={companyData.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {companyData.website}
                    </a>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="mb-3 text-lg font-semibold text-slate-900">Quick Stats</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Total Projects:</span>
                    <span className="font-semibold">{projects.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">My Clients:</span>
                    <span className="font-semibold">{clients.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">My Media Files:</span>
                    <span className="font-semibold">{media.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Launch Status:</span>
                    <span
                      className={`font-semibold ${companyData.isLaunched ? 'text-green-600' : 'text-orange-600'}`}
                    >
                      {companyData.isLaunched ? 'Launched' : 'Not Launched'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity Cards */}
            {(projects.length > 0 || media.length > 0 || clients.length > 0) && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">Recent Activity</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {/* Recent Projects */}
                  {projects.slice(0, 3).map(project => (
                    <div
                      key={project.id}
                      className="rounded-xl border border-slate-200 p-4 shadow-sm"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-slate-900">{project.name}</h4>
                          <p className="text-sm text-slate-600">{project.clientName}</p>
                          {project.clientEmail && (
                            <p className="text-xs text-slate-500">{project.clientEmail}</p>
                          )}
                        </div>
                        <span className="rounded-full bg-slate-100 px-2 py-1 text-xs capitalize text-slate-700">
                          {project.status?.replace('_', ' ') || 'planning'}
                        </span>
                      </div>
                      {project.description && (
                        <p className="mt-2 line-clamp-2 text-sm text-slate-600">
                          {project.description}
                        </p>
                      )}
                    </div>
                  ))}

                  {/* Recent Media */}
                  {media.slice(0, 3).map(item => (
                    <div key={item.id} className="rounded-xl border border-slate-200 p-3 shadow-sm">
                      <div className="relative aspect-video overflow-hidden rounded-lg">
                        {item.type === 'video' ? (
                          <video src={item.url} className="h-full w-full object-cover" />
                        ) : (
                          <img
                            src={item.url}
                            alt={item.caption || 'Media'}
                            className="h-full w-full object-cover"
                          />
                        )}
                      </div>
                      <div className="mt-2">
                        <p className="text-sm font-medium text-slate-900">
                          {item.caption || 'Media'}
                        </p>
                        <p className="text-xs text-slate-500">
                          {item.uploadedAt?.seconds
                            ? new Date(item.uploadedAt.seconds * 1000).toLocaleDateString()
                            : item.uploadedAt
                              ? new Date(item.uploadedAt).toLocaleDateString()
                              : ''}
                        </p>
                      </div>
                    </div>
                  ))}

                  {/* Recent Clients */}
                  {clients.slice(0, 3).map(client => (
                    <div
                      key={client.id}
                      className="rounded-xl border border-slate-200 p-4 shadow-sm"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-slate-900">{client.companyName}</h4>
                          {client.email && <p className="text-sm text-slate-600">{client.email}</p>}
                        </div>
                        <span className="rounded-full bg-slate-100 px-2 py-1 text-xs capitalize text-slate-700">
                          {client.status || 'active'}
                        </span>
                      </div>
                      {client.industry && (
                        <p className="mt-2 text-xs text-slate-500">Industry: {client.industry}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {activeSection === 'media' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Media Gallery</h3>
              <button
                onClick={() => setShowMediaUpload(true)}
                className="flex items-center space-x-2 rounded-lg bg-slate-900 px-3 py-1 text-sm text-white hover:opacity-90"
              >
                <Plus className="h-4 w-4" />
                <span>Upload Media</span>
              </button>
            </div>

            {media.length === 0 ? (
              <div className="rounded-xl border-2 border-dashed border-slate-300 p-8 text-center">
                <Image className="mx-auto mb-4 h-12 w-12 text-slate-400" />
                <p className="text-lg font-medium text-slate-900">No media uploaded yet</p>
                <p className="text-slate-600">Upload photos and videos to showcase your company</p>
                <button
                  onClick={() => setShowMediaUpload(true)}
                  className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-white hover:opacity-90"
                >
                  Upload First Media
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {media.map((item, index) => (
                  <div key={item.id} className="group relative">
                    {item.type === 'video' ? (
                      <div className="relative aspect-video overflow-hidden rounded-xl">
                        <video src={item.url} className="h-full w-full object-cover" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 transition-all group-hover:bg-opacity-50">
                          <Play className="h-8 w-8 text-white opacity-0 transition-opacity group-hover:opacity-100" />
                        </div>
                      </div>
                    ) : (
                      <img
                        src={item.url}
                        alt={item.caption || 'Media'}
                        className="h-48 w-full rounded-xl object-cover"
                      />
                    )}

                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 transition-all group-hover:bg-opacity-50">
                      <div className="flex space-x-2 opacity-0 transition-opacity group-hover:opacity-100">
                        <button
                          onClick={() => openMediaCarousel(index)}
                          className="rounded-full bg-white p-2 text-slate-900 hover:bg-slate-100"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteMedia(item.id)}
                          className="rounded-full bg-red-500 p-2 text-white hover:bg-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="mt-2">
                      <p className="text-sm font-medium text-slate-900">{item.caption}</p>
                      <p className="text-xs text-slate-500">
                        {item.uploadedAt?.seconds
                          ? new Date(item.uploadedAt.seconds * 1000).toLocaleDateString()
                          : new Date(item.uploadedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {activeSection === 'projects' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Projects</h3>
              <button
                onClick={() => setShowProjectForm(true)}
                className="flex items-center space-x-2 rounded-lg bg-slate-900 px-3 py-1 text-sm text-white hover:opacity-90"
              >
                <Plus className="h-4 w-4" />
                <span>Add Project</span>
              </button>
            </div>

            {projects.length === 0 ? (
              <div className="rounded-xl border-2 border-dashed border-slate-300 p-8 text-center">
                <Briefcase className="mx-auto mb-4 h-12 w-12 text-slate-400" />
                <p className="text-lg font-medium text-slate-900">No projects yet</p>
                <p className="text-slate-600">Add your first project to start tracking</p>
                <button
                  onClick={() => setShowProjectForm(true)}
                  className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-white hover:opacity-90"
                >
                  Add First Project
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {projects.map(project => (
                  <div
                    key={project.id}
                    className="rounded-xl border border-slate-200 p-4 transition-shadow hover:shadow-sm"
                  >
                    <div className="flex flex-col gap-4 md:flex-row">
                      {/* Project Images - Display first image or placeholder */}
                      {project.images && project.images.length > 0 ? (
                        <div className="md:w-48">
                          <div
                            onClick={() => openProjectImageCarousel(project, 0)}
                            className="group relative cursor-pointer overflow-hidden rounded-lg"
                          >
                            <img
                              src={project.images[0]?.url || project.images[0]}
                              alt={project.name}
                              className="h-32 w-full object-cover transition-transform group-hover:scale-105 md:h-40"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-all group-hover:bg-black/40">
                              <Eye className="h-6 w-6 text-white opacity-0 transition-opacity group-hover:opacity-100" />
                            </div>
                            {project.images.length > 1 && (
                              <div className="absolute right-2 top-2 rounded-full bg-black/60 px-2 py-1 text-xs text-white">
                                +{project.images.length - 1}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="md:w-48">
                          <div className="flex h-32 items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 md:h-40">
                            <Image className="h-8 w-8 text-slate-400" />
                          </div>
                        </div>
                      )}

                      <div className="flex flex-1 flex-col justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h4 className="font-semibold text-slate-900">{project.name}</h4>
                            <span
                              className={`rounded-full px-2 py-1 text-xs ${getStatusColor(project.status)}`}
                            >
                              {project.status?.replace('_', ' ') || 'Unknown'}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-slate-600">{project.description}</p>
                          <div className="mt-2 flex items-center space-x-4 text-xs text-slate-500">
                            <span>Client: {project.clientName}</span>
                            <span>Budget: ${project.budget}</span>
                            {project.startDate && (
                              <span>Start: {new Date(project.startDate).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                        <div className="mt-4 flex items-center justify-between">
                          <div className="text-right">
                            <p className="text-sm font-medium text-slate-900">${project.budget}</p>
                            <p className="text-xs text-slate-500">{project.currency}</p>
                          </div>
                          <div className="flex space-x-2">
                            {project.images && project.images.length > 0 && (
                              <button
                                onClick={() => openProjectImageCarousel(project, 0)}
                                className="rounded-lg bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
                                title="Preview Images"
                              >
                                <Eye className="mr-1 inline h-3 w-3" />
                                View Images
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setEditingProject(project);
                                setShowProjectForm(true);
                              }}
                              className="rounded-lg bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
                            >
                              Edit
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  await deleteDoc(
                                    doc(
                                      firestore,
                                      'companyProfiles',
                                      organizationId,
                                      'projects',
                                      project.id
                                    )
                                  );
                                } catch (err) {
                                  alert('Failed to delete project');
                                }
                              }}
                              className="rounded-lg bg-red-50 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {activeSection === 'clients' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Clients</h3>
              <button
                onClick={() => setShowClientForm(true)}
                className="flex items-center space-x-2 rounded-lg bg-slate-900 px-3 py-1 text-sm text-white hover:opacity-90"
              >
                <Plus className="h-4 w-4" />
                <span>Add Client</span>
              </button>
            </div>

            {clients.length === 0 ? (
              <div className="rounded-xl border-2 border-dashed border-slate-300 p-8 text-center">
                <Users className="mx-auto mb-4 h-12 w-12 text-slate-400" />
                <p className="text-lg font-medium text-slate-900">No clients yet</p>
                <p className="text-slate-600">
                  Add your first client to start building relationships
                </p>
                <button
                  onClick={() => setShowClientForm(true)}
                  className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-white hover:opacity-90"
                >
                  Add First Client
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {clients.map(client => (
                  <div key={client.id} className="rounded-xl border border-slate-200 p-4 shadow-sm">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-slate-900">
                          {client.companyName || client.organization}
                        </h4>
                        {client.email && <p className="text-sm text-slate-600">{client.email}</p>}
                        {client.industry && (
                          <p className="text-xs text-slate-500">{client.industry}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setViewClient(client);
                            setShowClientView(true);
                          }}
                          className="rounded-full bg-slate-100 p-2 text-slate-700 hover:bg-slate-200"
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingClient(client);
                            setShowClientForm(true);
                          }}
                          className="rounded-full bg-blue-100 p-2 text-blue-700 hover:bg-blue-200"
                          title="Edit"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              const path =
                                client._source === 'user'
                                  ? ['companyClient', currentUserId, 'clients', client.id]
                                  : ['companyProfiles', organizationId, 'clients', client.id];
                              await deleteDoc(doc(firestore, ...path));
                            } catch (err) {
                              alert('Failed to delete client');
                            }
                          }}
                          className="rounded-full bg-red-100 p-2 text-red-700 hover:bg-red-200"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                      {client.contactPerson && (
                        <div>
                          <span className="text-slate-500">Contact:</span> {client.contactPerson}
                        </div>
                      )}
                      {client.phone && (
                        <div>
                          <span className="text-slate-500">Phone:</span> {client.phone}
                        </div>
                      )}
                      {client.city && (
                        <div>
                          <span className="text-slate-500">City:</span> {client.city}
                        </div>
                      )}
                      {client.country && (
                        <div>
                          <span className="text-slate-500">Country:</span> {client.country}
                        </div>
                      )}
                      {client.businessStage && (
                        <div className="col-span-2">
                          <span className="text-slate-500">Stage:</span> {client.businessStage}
                        </div>
                      )}
                    </div>

                    <div className="mt-3 flex items-center justify-between">
                      <span
                        className={`rounded-full px-2 py-1 text-xs ${getKYCStatusColor(client.kycStatus)}`}
                      >
                        KYC: {client.kycStatus?.replace('_', ' ') || 'Pending'}
                      </span>
                      <span
                        className={`rounded-full px-2 py-1 text-xs ${
                          client.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {client.status || 'Active'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Modals */}
      {showMediaUpload && (
        <MediaUpload
          organizationId={organizationId}
          onUploadComplete={handleMediaUploadComplete}
          onClose={() => setShowMediaUpload(false)}
        />
      )}

      {showMediaCarousel && (
        <MediaCarousel
          media={media}
          onClose={() => setShowMediaCarousel(false)}
          initialIndex={selectedMediaIndex}
        />
      )}

      {showProjectImageCarousel && (
        <MediaCarousel
          media={selectedProjectImages}
          initialIndex={selectedProjectImageIndex}
          onClose={() => {
            setShowProjectImageCarousel(false);
            setSelectedProjectImages([]);
            setSelectedProjectImageIndex(0);
          }}
        />
      )}

      {showClientForm && (
        <ClientForm
          organizationId={organizationId}
          currentUserId={currentUserId}
          onClientAdded={handleClientAdded}
          onClose={() => setShowClientForm(false)}
          editingClient={editingClient}
          useGlobalClients
        />
      )}

      {showProjectForm && (
        <ProjectForm
          organizationId={organizationId}
          currentUserId={currentUserId}
          onProjectAdded={handleProjectAdded}
          onClose={() => setShowProjectForm(false)}
          editingProject={editingProject}
        />
      )}

      {/* Client View Modal */}
      {showClientView && viewClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">
                {viewClient.companyName || viewClient.organization}
              </h3>
              <button
                onClick={() => {
                  setShowClientView(false);
                  setViewClient(null);
                }}
                className="rounded-full p-2 hover:bg-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {viewClient.contactPerson && (
                <div>
                  <div className="text-xs text-slate-500">Contact Person</div>
                  <div className="text-sm font-medium text-slate-900">
                    {viewClient.contactPerson}
                  </div>
                </div>
              )}
              {viewClient.email && (
                <div>
                  <div className="text-xs text-slate-500">Email</div>
                  <div className="text-sm font-medium text-slate-900">{viewClient.email}</div>
                </div>
              )}
              {viewClient.phone && (
                <div>
                  <div className="text-xs text-slate-500">Phone</div>
                  <div className="text-sm font-medium text-slate-900">{viewClient.phone}</div>
                </div>
              )}
              {viewClient.industry && (
                <div>
                  <div className="text-xs text-slate-500">Industry</div>
                  <div className="text-sm font-medium text-slate-900">{viewClient.industry}</div>
                </div>
              )}
              {viewClient.businessStage && (
                <div className="md:col-span-2">
                  <div className="text-xs text-slate-500">Business Stage</div>
                  <div className="text-sm font-medium text-slate-900">
                    {viewClient.businessStage}
                  </div>
                </div>
              )}
              {viewClient.address && (
                <div className="md:col-span-2">
                  <div className="text-xs text-slate-500">Address</div>
                  <div className="text-sm font-medium text-slate-900">{viewClient.address}</div>
                </div>
              )}
              {viewClient.notes && (
                <div className="md:col-span-2">
                  <div className="text-xs text-slate-500">Notes</div>
                  <div className="text-sm text-slate-700">{viewClient.notes}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
